/**
 * @module immersive/ArrowFace
 * @description The visual face of a Legend Arrow: heart-tipped arrow glyph,
 * US$ value, Legend name. Twin arrows show a doubled shaft. Tokens only.
 */
import type { JSX } from "react";
import { isTwin, type LegendArrow } from "../domain/arrows";

/** Heart-tipped arrow glyph (currentColor); doubled for Twin arrows. */
export function ArrowGlyph({ twin = false, size = 28 }: { twin?: boolean; size?: number }): JSX.Element {
    const shaft = (y: number): JSX.Element => (
        <g key={y}>
            <path d={`M3 ${y} H22`} strokeWidth={1.8} strokeLinecap="round" />
            <path d={`M3 ${y} l3 -3 M3 ${y} l3 3 M6 ${y} l3 -3 M6 ${y} l3 3`} strokeWidth={1.4} strokeLinecap="round" />
            <path d={`M22 ${y - 3.2} c1.6 -1.8 4.6 -0.8 4.4 1.6 c-0.2 2 -2.6 3.4 -4.4 4.8 c-1.8 -1.4 -4.2 -2.8 -4.4 -4.8 c-0.2 -2.4 2.8 -3.4 4.4 -1.6 Z`} fill="currentColor" stroke="none" />
        </g>
    );
    return (
        <svg width={size} height={size} viewBox="0 0 30 30" fill="none" stroke="currentColor" aria-hidden="true" focusable="false">
            {twin ? [shaft(10), shaft(20)] : shaft(15)}
        </svg>
    );
}

/** Arrow face: glyph + "US$20" + "Garvey Legend". */
export function ArrowFace({ arrow, count }: { arrow: LegendArrow; count?: number }): JSX.Element {
    return (
        <span className={`arrow-face${isTwin(arrow) ? " arrow-face--twin" : ""}${arrow.availability === "rebel" ? " arrow-face--rebel" : ""}`}>
            <ArrowGlyph twin={isTwin(arrow)} />
            <span className="arrow-face__value">US${arrow.faceUsd.toLocaleString("en-US")}</span>
            <span className="arrow-face__name">{arrow.name} Legend</span>
            {count !== undefined && count > 1 && <span className="arrow-face__count">×{count}</span>}
        </span>
    );
}
